import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Typography,
  Button,
  Alert,
  Pagination,
  Fab,
  Paper,
  Skeleton,
  Snackbar,
  Chip,
} from '@mui/material';
import {
  Refresh,
  PersonAdd,
  AdminPanelSettings,
  ManageAccounts,
  Engineering,
  CheckCircle,
  Block,
} from '@mui/icons-material';

import { useAuth, useRole } from '../../contexts/AuthContext';
import { UserRole } from "../../types";
import type { User, UserCreateInput, SearchFilters, SortOptions, PaginatedResponse } from "../../types";
import apiClient from '../../services/api';
import UserCard from '../../components/users/UserCard';
import UserFilters from '../../components/users/UserFilters';
import {
  UserActivityDialog,
  UserConfirmDialog,
  UserFormDialog,
  UserPasswordDialog,
  UserRoleDialog,
} from '../../components/users/UserManagementDialogs';
import { invalidateUserData } from '../../utils/cacheInvalidation';

const UsersPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { canManageUsers } = useRole();
  const queryClient = useQueryClient();

  // Состояния для UI
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(12);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Состояния для фильтрации и сортировки
  const [filters, setFilters] = useState<SearchFilters>({
    search: '',
    userRoles: [],
    activeUsersOnly: false,
    inactiveUsersOnly: false,
    onlineUsersOnly: false,
  });

  const [sortOptions, setSortOptions] = useState<SortOptions>({
    field: 'full_name',
    direction: 'asc',
  });

  const { data: usersResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['users', currentPage, pageSize, filters, sortOptions],
    queryFn: async (): Promise<PaginatedResponse<User>> => apiClient.getUsers(filters, sortOptions, currentPage, pageSize),
    refetchInterval: 60000, // Обновляем каждую минуту
  });

  const { data: userStats } = useQuery({
    queryKey: ['user-stats'],
    queryFn: () => apiClient.getUserStats(),
    refetchInterval: 60000,
  });

  const { data: activityResponse, isLoading: isActivityLoading } = useQuery({
    queryKey: ['user-activity', selectedUser?.id],
    queryFn: () => apiClient.getUserActivity(selectedUser!.id),
    enabled: activityDialogOpen && Boolean(selectedUser?.id),
  });

  const invalidateUsers = () => {
    invalidateUserData(queryClient);
  };

  const createUserMutation = useMutation({
    mutationFn: apiClient.createUser.bind(apiClient),
    onSuccess: invalidateUsers,
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<User> }) => apiClient.updateUser(id, data),
    onSuccess: invalidateUsers,
  });

  const deleteUserMutation = useMutation({
    mutationFn: apiClient.deleteUser.bind(apiClient),
    onSuccess: invalidateUsers,
  });

  const changeRoleMutation = useMutation({
    mutationFn: ({ id, role }: { id: number; role: UserRole }) => apiClient.changeUserRole(id, role),
    onSuccess: invalidateUsers,
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (targetUser: User) => targetUser.is_active
      ? apiClient.deactivateUser(targetUser.id)
      : apiClient.activateUser(targetUser.id),
    onSuccess: invalidateUsers,
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, password }: { id: number; password: string }) => apiClient.resetUserPassword(id, password),
  });

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getErrorMessage = (err: any, fallback: string) => {
    const detail = err?.response?.data?.detail || err?.detail;
    if (Array.isArray(detail)) {
      return detail.map((item) => item.msg || item.message || String(item)).join(', ');
    }
    return detail || err?.message || fallback;
  };

  const closeDialogs = () => {
    setFormOpen(false);
    setRoleDialogOpen(false);
    setPasswordDialogOpen(false);
    setActivityDialogOpen(false);
    setConfirmDeleteOpen(false);
    setDialogError(null);
    setSelectedUser(null);
  };

  // Обработчики событий
  const handleFiltersChange = (newFilters: SearchFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleSortChange = (newSort: SortOptions) => {
    setSortOptions(newSort);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      userRoles: [],
      activeUsersOnly: false,
      inactiveUsersOnly: false,
      onlineUsersOnly: false,
    });
    setCurrentPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Действия с пользователями
  const handleUserEdit = (userToEdit: User) => {
    setSelectedUser(userToEdit);
    setFormMode('edit');
    setDialogError(null);
    setFormOpen(true);
  };

  const handleUserDelete = (userToDelete: User) => {
    setSelectedUser(userToDelete);
    setDialogError(null);
    setConfirmDeleteOpen(true);
  };

  const handleToggleStatus = async (userToToggle: User) => {
    try {
      const updatedUser = await toggleStatusMutation.mutateAsync(userToToggle);
      showSnackbar(
        updatedUser.is_active
        ? t('users.userActivated', { name: userToToggle.full_name || userToToggle.username })
        : t('users.userDeactivated', { name: userToToggle.full_name || userToToggle.username })
      );
    } catch (err: any) {
      showSnackbar(getErrorMessage(err, t('common.error')), 'error');
    }
  };

  const handleResetPassword = (userToReset: User) => {
    setSelectedUser(userToReset);
    setDialogError(null);
    setPasswordDialogOpen(true);
  };

  const handleViewActivity = (userToView: User) => {
    setSelectedUser(userToView);
    setDialogError(null);
    setActivityDialogOpen(true);
  };

  const handleChangeRole = (userToChange: User) => {
    setSelectedUser(userToChange);
    setDialogError(null);
    setRoleDialogOpen(true);
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setFormMode('create');
    setDialogError(null);
    setFormOpen(true);
  };

  const handleSubmitUserForm = async (value: UserCreateInput | Partial<User>) => {
    try {
      if (formMode === 'create') {
        await createUserMutation.mutateAsync(value as UserCreateInput);
        showSnackbar('Пользователь создан');
      } else if (selectedUser) {
        await updateUserMutation.mutateAsync({
          id: selectedUser.id,
          data: value as Partial<User>,
        });
        showSnackbar('Пользователь обновлен');
      }
      closeDialogs();
    } catch (err: any) {
      setDialogError(getErrorMessage(err, t('common.error')));
    }
  };

  const handleSubmitRole = async (role: UserRole) => {
    if (!selectedUser) return;
    try {
      await changeRoleMutation.mutateAsync({ id: selectedUser.id, role });
      showSnackbar('Роль пользователя изменена');
      closeDialogs();
    } catch (err: any) {
      setDialogError(getErrorMessage(err, t('common.error')));
    }
  };

  const handleSubmitPassword = async (password: string) => {
    if (!selectedUser) return;
    try {
      await resetPasswordMutation.mutateAsync({ id: selectedUser.id, password });
      showSnackbar(t('users.passwordReset', { name: selectedUser.full_name || selectedUser.username }));
      closeDialogs();
    } catch (err: any) {
      setDialogError(getErrorMessage(err, t('common.error')));
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;
    try {
      await deleteUserMutation.mutateAsync(selectedUser.id);
      showSnackbar(t('users.deleteSuccess', { name: selectedUser.full_name || selectedUser.username }));
      closeDialogs();
    } catch (err: any) {
      setDialogError(getErrorMessage(err, t('common.error')));
    }
  };

  // Загрузочные скелетоны
  const renderSkeletons = () => (
    <Grid container spacing={2}>
      {Array.from({ length: pageSize }).map((_, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
          <Paper elevation={1} sx={{ p: 2, height: 350 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Skeleton variant="circular" width={56} height={56} />
              <Box sx={{ flexGrow: 1 }}>
                <Skeleton variant="text" height={24} width="80%" />
                <Skeleton variant="text" height={16} width="60%" />
              </Box>
            </Box>
            <Skeleton variant="text" height={16} width="40%" sx={{ mb: 1 }} />
            <Skeleton variant="text" height={16} width="70%" sx={{ mb: 1 }} />
            <Skeleton variant="text" height={16} width="50%" sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={40} />
          </Paper>
        </Grid>
      ))}
    </Grid>
  );

  // Отображение пользователей
  const renderUsersGrid = () => {
    if (!usersResponse?.items.length) {
      return (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('users.noUsers')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('users.noUsersDescription')}
          </Typography>
          {canManageUsers() && (
            <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={handleCreateUser}
            >
              {t('users.createFirst')}
            </Button>
          )}
        </Paper>
      );
    }

    return (
      <Grid container spacing={2}>
        {usersResponse.items.map((userItem) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={userItem.id}>
            <UserCard
              user={userItem}
              currentUserRole={user?.role}
              currentUserId={user?.id}
              onEdit={handleUserEdit}
              onDelete={canManageUsers() ? handleUserDelete : undefined}
              onToggleStatus={canManageUsers() ? handleToggleStatus : undefined}
              onResetPassword={handleResetPassword}
              onViewActivity={handleViewActivity}
              onChangeRole={canManageUsers() ? handleChangeRole : undefined}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  // Статистика в заголовке
  const getRoleCount = (role: UserRole) => {
    return userStats?.by_role?.[role] ?? 0;
  };

  const getActiveCount = () => {
    return userStats?.active_users ?? 0;
  };

  const getOnlineCount = () => {
    return userStats?.online_users ?? 0;
  };

  return (
    <Box>
      {/* Заголовок страницы */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            {t('navigation.users')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {t('users.pageDescription')}
          </Typography>
          
          {/* Быстрая статистика */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<CheckCircle />}
              label={`${getActiveCount()} ${t('users.active')}`}
              color="success"
              variant="outlined"
            />
            <Chip
              icon={<Block />}
              label={`${userStats?.inactive_users ?? 0} ${t('users.inactive')}`}
              color="error"
              variant="outlined"
            />
            <Chip
              icon={<AdminPanelSettings />}
              label={`${getRoleCount(UserRole.ADMIN)} ${t('users.admins')}`}
              color="error"
              variant="outlined"
            />
            <Chip
              icon={<ManageAccounts />}
              label={`${getRoleCount(UserRole.MANAGER)} ${t('users.managers')}`}
              color="warning"
              variant="outlined"
            />
            <Chip
              icon={<Engineering />}
              label={`${getOnlineCount()} ${t('users.online')}`}
              color="info"
              variant="outlined"
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Кнопка обновления */}
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            {t('common.refresh')}
          </Button>

          {/* Кнопка создания */}
          {canManageUsers() && (
            <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={handleCreateUser}
            >
              {t('users.createUser')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Фильтры */}
      <UserFilters
        filters={filters}
        sortOptions={sortOptions}
        onFiltersChange={handleFiltersChange}
        onSortChange={handleSortChange}
        onClearFilters={handleClearFilters}
        totalItems={usersResponse?.total || 0}
        stats={userStats}
      />

      {/* Ошибка загрузки */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('common.loadError')}
        </Alert>
      )}

      {/* Контент страницы */}
      {isLoading ? renderSkeletons() : renderUsersGrid()}

      {/* Пагинация */}
      {usersResponse && usersResponse.total_pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={usersResponse.total_pages}
            page={currentPage}
            onChange={handlePageChange}
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* FAB для мобильных устройств */}
      {canManageUsers() && (
        <Fab
          color="primary"
          aria-label="create user"
          onClick={handleCreateUser}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', md: 'none' },
          }}
        >
          <PersonAdd />
        </Fab>
      )}

      {/* Уведомления */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <UserFormDialog
        open={formOpen}
        mode={formMode}
        user={selectedUser}
        loading={createUserMutation.isPending || updateUserMutation.isPending}
        error={dialogError}
        onClose={closeDialogs}
        onSubmit={handleSubmitUserForm}
      />

      <UserRoleDialog
        open={roleDialogOpen}
        user={selectedUser}
        loading={changeRoleMutation.isPending}
        error={dialogError}
        onClose={closeDialogs}
        onSubmit={handleSubmitRole}
      />

      <UserPasswordDialog
        open={passwordDialogOpen}
        user={selectedUser}
        loading={resetPasswordMutation.isPending}
        error={dialogError}
        onClose={closeDialogs}
        onSubmit={handleSubmitPassword}
      />

      <UserActivityDialog
        open={activityDialogOpen}
        user={selectedUser}
        logs={activityResponse?.items || []}
        loading={isActivityLoading}
        onClose={closeDialogs}
      />

      <UserConfirmDialog
        open={confirmDeleteOpen}
        title="Деактивировать пользователя"
        message={`Пользователь ${selectedUser?.full_name || selectedUser?.username || ''} будет деактивирован и не сможет войти в систему.`}
        confirmLabel="Деактивировать"
        loading={deleteUserMutation.isPending}
        onClose={closeDialogs}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};

export default UsersPage;
